/**
 * The furniture the onboarding screens share (PRD FR-1.1, rulings C5, R-0826-3).
 *
 * THE COUNTER DERIVES ITS TOTAL FROM THE PATH. A door owner runs four steps and
 * a knocker runs three, and a knocker must never be shown "3 of 4 · Rules" for a
 * step they will not reach — FR-1.1 calls that "a small thing that reads as a
 * broken product at the exact moment somebody is deciding whether to pay", which
 * is the whole argument for the total being computed rather than written.
 *
 * EMAIL SITS SECOND, immediately after Connect (ruling R-0826-3). It went there
 * rather than into a first CTA in the console because everything Nob is obliged
 * to write — a receipt, a dunning notice, the Morning Nob, a reply arriving —
 * reads that one address, and a prompt that can be ignored for ever was
 * producing accounts the product could not talk to. It is before the money step
 * for the same reason: the first thing that follows a card is a receipt.
 *
 * The splash is the run's loading state and nothing else. It exists because the
 * first thing a signed-in visitor's browser does is ask the server where they
 * are, and a blank frame while it answers reads as a product that has not
 * started.
 */
import type { ReactNode } from "react";
import { useEffect } from "react";
import { PosterFace, PosterPage, PosterStep } from "../ui/poster";

/** The labels, and therefore the count. The two can never disagree. */
export const OWNER_STEPS = ["Connect", "Email", "Trial", "Rules"] as const;
export const KNOCKER_STEPS = ["Connect", "Email", "Trial"] as const;

export const SPLASH_LINE = "Hold on. Checking the door.";

export type StepNumber = 1 | 2 | 3 | 4;

/**
 * AND THE POSITION IS CLAMPED TO THE RUN, WITH ITS OWN NAME (browser walk,
 * 2026-09-10).
 *
 * The total was derived and the position was not, which left one screen able to
 * contradict it: a knocker who stepped aside from the door step (§2.3.3) had
 * been counted through a three-step run — "1 of 3", "2 of 3", "3 of 3" — and
 * then came back for the deferred step and was shown "4 OF 4 · RULES". A run
 * that grows a fourth step the moment somebody returns to it is the same broken
 * arithmetic the header above warns about, pointing the other way.
 *
 * So a step past the end of the run is the LAST step of that run, and the
 * screen that knows what it is called says so — a knocker's deferred rules step
 * reads "3 of 3 · Rules", an owner's reads "4 of 4 · Rules", and neither
 * counter ever names a total the person was not shown.
 */
export function StepDots({
  step,
  knocker,
  label,
}: {
  step: StepNumber;
  knocker: boolean;
  /** The step's own name, where the run's list does not hold it. */
  label?: string;
}) {
  const labels = knocker ? KNOCKER_STEPS : OWNER_STEPS;
  const total = labels.length;
  const at = Math.min(step, total);
  return <PosterStep step={at} total={total} label={label ?? labels[at - 1] ?? ""} />;
}

/**
 * The printed sheet every onboarding screen stands on (ui/poster.tsx).
 *
 * It used to be a plain centred column on the app's own ground, and that was
 * the mistake the whole restyle is fixing: these screens are the brand, not the
 * chrome. The name stays `OnboardShell` because that is what the run calls it;
 * what it renders is a poster.
 */
export function OnboardShell({ children, about }: { children: ReactNode; about?: boolean }) {
  return <PosterPage about={about}>{children}</PosterPage>;
}

/** Nob, on his plate, at the size the arrival screens use him. */
export function OnboardFace({ mood }: { mood: "watching" | "pleased" | "doubting" }) {
  return <PosterFace mood={mood} />;
}

/**
 * THE DEMAND LINE (owner item 3, 2026-08-27; WR objective O6, FR-G9.1).
 *
 * The waiting room's whole point is that a door opens to visible proof somebody
 * was already asking, and until now that proof arrived only AFTER the run — in
 * the console, once the door existed. Anybody who closed the tab at the money
 * step never learned they had six people standing outside. So it is said on the
 * two screens the Connect step can already answer for: the run knows the handle
 * from the moment the identity comes back, and the count is one read away.
 *
 * THE FIGURE IS SHOWN ONCE, AND ONLY TO THE OWNER (ruling R-0916-3, ToU 2.2
 * §5A.5, Privacy 2.2 §2A.2: "shown only to the owner of that handle, when they
 * open their door"). This badge appears on two screens of the run; the DOOR
 * step is the one that may print the number, because that is the door being
 * opened, and the email step says only that somebody is there. Nothing stronger
 * than the count may appear on either: no lines, no names, no positions, no
 * pending rows. The registrants' lines ARE the owner's to read, but they are
 * the conversion preview's business (WR FR-G5.2 #9) and that is AI work behind
 * a plan; the count is not.
 *
 * ABSENT AT ZERO. "Nobody is waiting yet" on the second screen of somebody's
 * first run is a sentence about their popularity that nobody asked this product
 * to write.
 */
export function waitingAtYourDoorLine(count: number): string {
  return count === 1
    ? "1 person is already waiting at your door."
    : `${count} people are already waiting at your door.`;
}

/** The same fact without the figure — the email step's half of the reveal. */
export const SOMEBODY_WAITING_LINE = "Somebody is already waiting at your door.";

/**
 * The one line after it: why the rest of the run is worth answering.
 *
 * It says FINISH rather than naming a step, because the badge appears on two of
 * them and a sentence that named one would be wrong on the other.
 */
export const WAITING_AT_DOOR_HINT = "Finish setting up and they are the first thing you see.";

function Badge({ headline }: { headline: string }) {
  return (
    <p className="nob-poster-demand" role="status">
      <strong>{headline}</strong>
      <span>{WAITING_AT_DOOR_HINT}</span>
    </p>
  );
}

/** Door setup: the owner reveal, figure and all. Nowhere else. */
export function WaitingAtDoor({ count }: { count: number }) {
  if (count <= 0) return null;
  return <Badge headline={waitingAtYourDoorLine(count)} />;
}

/** Every other screen of the run: the same news, told without the number. */
export function SomebodyWaiting({ count }: { count: number }) {
  if (count <= 0) return null;
  return <Badge headline={SOMEBODY_WAITING_LINE} />;
}

export function OnboardingSplash() {
  return (
    <OnboardShell>
      <OnboardFace mood="watching" />
      <p className="nob-breathe nob-poster-lead">{SPLASH_LINE}</p>
    </OnboardShell>
  );
}

/**
 * A sheet over the step, for the card form and the skip confirmation (FR-5.9).
 *
 * It is a layer rather than a fourth screen on purpose: both things it carries
 * are answers to the step underneath, and stepping away to a page of their own
 * would lose the question they answer.
 */
export function OnboardSheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="nob-poster-scrim"
      onClick={onClose}
    >
      <div className="nob-poster-sheet" onClick={(e) => e.stopPropagation()}>
        <p className="nob-poster-sheet-title">{title}</p>
        {children}
      </div>
    </div>
  );
}
